<template>
  <v-expansion-panel class="EnvelopeTemplateAddSettingsDocument-expansionPanel" elevation="0">
    <v-expansion-panel-title>
      <SigningChecklistDocumentHeader
        :file="file"
        :validator="v$"
        @deleteFile="isDeleteFileDialogOpened = true"
      />

      <template #actions="{ expanded }">
        <app-material-icon
          v-if="expanded"
          name="keyboard_arrow_up"
          color="icon-secondary"
        />
        <app-material-icon
          v-else
          name="keyboard_arrow_down"
          color="icon-secondary"
        />
      </template>
    </v-expansion-panel-title>
    <v-expansion-panel-text>
      <template v-if="v$.file.objectToPost.$error">
        <AppAlertInPage variant="error">
          {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddSettingsDocument.validatorAlertContent') }}
        </AppAlertInPage>
      </template>

      <SigningChecklistSection>
        <template #title>
          1. {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddSettingsDocument.sectionTitles.documentTitle') }}
          <span class="text-indigo">*</span>
        </template>
        <template #content>
          <SigningChecklistSectionDocumentTitle
            :file-name="file.objectToPost.title"
            :is-editing-limited="false"
            :title-validator="v$.file.objectToPost.title"
            @title-change="setTemplateDocumentTitle"
          />
        </template>
      </SigningChecklistSection>

      <SigningChecklistSection>
        <template #title>
          2. {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddSettingsDocument.sectionTitles.signatureType') }}
          <span class="text-indigo">*</span>
        </template>
        <template #content>
          <SigningChecklistSectionDocumentSignatureType
            :file-size="file.size"
            :has-mercury-option-activated="false"
            :has-signers-order="false"
            :is-editing-limited="false"
            :signature-type-validator="v$.file.objectToPost.signatureType"
            :signature-type="file.objectToPost.signatureType"
            is-handwritten-signature-disabled
            @setQualifiedDSWarningDialog="isQualifiedDSWarningDialogOpened = $event"
            @signature-type-change="setTemplateDocumentSignatureType"
          />
        </template>
      </SigningChecklistSection>

      <SigningChecklistSection>
        <template #title>
          3. {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddSettingsDocument.sectionTitles.envelopeType') }}
          <span class="text-indigo">*</span>
        </template>
        <template #content>
          <SigningChecklistSectionDocumentEnvelopeType
            :envelope-type-validator="v$.file.objectToPost.envelopeType"
            :envelope-type="file.objectToPost.envelopeType"
            :is-editing-limited="false"
            :is-multi-envelope-child="false"
            @envelope-type-change="setTemplateDocumentEnvelopeType"
          />
        </template>
      </SigningChecklistSection>

      <SigningChecklistSection>
        <template #title>
          4. {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddSettingsDocument.sectionTitles.signersCount') }}
          <span v-if="file.objectToPost.envelopeType === ENVELOPE_TYPES.SINGLE" class="text-indigo">*</span>
        </template>
        <template #content>
          <SigningChecklistSectionDocumentSignersCount
            :file-envelope-type="file.objectToPost.envelopeType"
            :file-signers="file.objectToPost.signers"
            :is-editing-limited="false"
            :signers-validator="v$.file.objectToPost.signers"
            @signers-change="setTemplateDocumentSigners"
          />
        </template>
      </SigningChecklistSection>

      <SigningChecklistSection>
        <template #title>
          5. {{ $t('project.signing-checklist.envelope-templates.add.EnvelopeTemplateAddSettingsDocument.sectionTitles.sharingOptions') }}
        </template>
        <template #content>
          <SigningChecklistSectionDocumentSharingOptions
            :share-type="file.objectToPost.shareType"
            :file-rights="file.objectToPost.rights || { isCustom : true }"
            :file-with-users="file.objectToPost.withUsers"
            :is-editing-limited="false"
            @send-notif-change="setTemplateDocumentShareSendNotif"
            @share-type-change="setTemplateDocumentShareType"
            @with-users-change="setTemplateDocumentShareWithUsers"
          />
        </template>
      </SigningChecklistSection>
    </v-expansion-panel-text>

    <QualifiedDSSignatureWarningDialog
      v-if="isQualifiedDSWarningDialogOpened"
      @close="isQualifiedDSWarningDialogOpened = false"
      @confirm="onConfirmQualifiedSignatureSelection"
    />

    <DeleteFileDialog
      v-if="isDeleteFileDialogOpened"
      @close="isDeleteFileDialogOpened = false"
      @confirm="onConfirmDeleteFile"
    />
  </v-expansion-panel>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { maxValue, numeric, required } from '@vuelidate/validators'
import { defineComponent } from 'vue'

import { SHARE_TYPE_OPTIONS } from '@/common/utils/shareTypes'
import { typedMapMutations } from '@/common/utils/store'
import AppAlertInPage from '@/design-system/alert/AppAlertInPage.vue'
import SigningChecklistDocumentHeader from '@/project/signing-checklist/add/SigningChecklistDocumentHeader.vue'
import SigningChecklistSection from '@/project/signing-checklist/add/SigningChecklistSection.vue'
import SigningChecklistSectionDocumentEnvelopeType from '@/project/signing-checklist/add/SigningChecklistSectionDocumentEnvelopeType.vue'
import SigningChecklistSectionDocumentSharingOptions from '@/project/signing-checklist/add/SigningChecklistSectionDocumentSharingOptions.vue'
import SigningChecklistSectionDocumentSignatureType from '@/project/signing-checklist/add/SigningChecklistSectionDocumentSignatureType.vue'
import SigningChecklistSectionDocumentSignersCount from '@/project/signing-checklist/add/SigningChecklistSectionDocumentSignersCount.vue'
import SigningChecklistSectionDocumentTitle from '@/project/signing-checklist/add/SigningChecklistSectionDocumentTitle.vue'
import { ENVELOPE_TYPES } from '@/project/signing-checklist/constants'
import QualifiedDSSignatureWarningDialog from '@/project/signing-checklist/dialogs/QualifiedDSSignatureWarningDialog.vue'
import DeleteFileDialog from '@/project/signing-checklist/envelope-templates/dialogs/DeleteFileDialog.vue'

const sharedWithValidator = (currentUserId) => {
  return (withUsers, objectToPostModel) => {
    if (objectToPostModel.shareType === SHARE_TYPE_OPTIONS.CUSTOM) {
      return withUsers.filter(userId => userId !== currentUserId).length > 0
    }

    return true
  }
}

export default defineComponent({
  name: 'EnvelopeTemplateAddSettingsDocument',
  components: {
    AppAlertInPage,
    DeleteFileDialog,
    QualifiedDSSignatureWarningDialog,
    SigningChecklistDocumentHeader,
    SigningChecklistSection,
    SigningChecklistSectionDocumentEnvelopeType,
    SigningChecklistSectionDocumentSharingOptions,
    SigningChecklistSectionDocumentSignatureType,
    SigningChecklistSectionDocumentSignersCount,
    SigningChecklistSectionDocumentTitle,
  },
  setup: () => ({ v$: useVuelidate() }),
  props: {
    file: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      isDeleteFileDialogOpened: false,
      isQualifiedDSWarningDialogOpened: false,
      ENVELOPE_TYPES,
    }
  },
  validations () {
    const signersValidator = {
      maxValue: maxValue(150),
      minValue: (value, model) => {
        if (model.envelopeType === ENVELOPE_TYPES.SINGLE) {
          return value >= 1
        }

        return true
      },
      numeric,
      required,
    }

    const objectToPostValidations = {
      title: {
        required,
      },
      signatureType: {
        required,
      },
      signers: signersValidator,
      envelopeType: {
        required,
      },
      shareType: {
        required,
      },
      withUsers: {
        required: sharedWithValidator(this.currentUserId),
      },
    }

    return {
      file: {
        objectToPost: objectToPostValidations,
      },
    }
  },
  methods: {
    ...typedMapMutations('envelopeTemplates', [
      'REMOVE_DOCUMENT_FROM_TEMPLATE_DRAFT',
      'SET_TEMPLATE_DOCUMENT_ENVELOPE_TYPE',
      'SET_TEMPLATE_DOCUMENT_SHARE_SEND_NOTIF',
      'SET_TEMPLATE_DOCUMENT_SHARE_TYPE',
      'SET_TEMPLATE_DOCUMENT_SHARE_WITH_USERS',
      'SET_TEMPLATE_DOCUMENT_SIGNATURE_TYPE',
      'SET_TEMPLATE_DOCUMENT_SIGNERS',
      'SET_TEMPLATE_DOCUMENT_TITLE',
    ]),
    onConfirmDeleteFile (file) {
      this.isDeleteFileDialogOpened = false
      this.REMOVE_DOCUMENT_FROM_TEMPLATE_DRAFT(file)
    },
    onConfirmQualifiedSignatureSelection () {
      this.isQualifiedDSWarningDialogOpened = false
      this.setTemplateDocumentSignatureType('ds.qualified')
    },
    setTemplateDocumentEnvelopeType (envelopeType) {
      this.SET_TEMPLATE_DOCUMENT_ENVELOPE_TYPE({
        fileId: this.file.id,
        envelopeType: envelopeType,
      })
    },
    setTemplateDocumentShareSendNotif (sendNotif) {
      this.SET_TEMPLATE_DOCUMENT_SHARE_SEND_NOTIF({
        fileId: this.file.id,
        sendNotif: sendNotif,
      })
    },
    setTemplateDocumentShareType (shareType) {
      this.SET_TEMPLATE_DOCUMENT_SHARE_TYPE({
        fileId: this.file.id,
        shareType: shareType,
      })
    },
    setTemplateDocumentShareWithUsers (withUsers) {
      this.SET_TEMPLATE_DOCUMENT_SHARE_WITH_USERS({
        fileId: this.file.id,
        withUsers: withUsers,
      })
    },
    setTemplateDocumentSignatureType (signatureType) {
      this.SET_TEMPLATE_DOCUMENT_SIGNATURE_TYPE({
        fileId: this.file.id,
        signatureType: signatureType,
      })

      this.$emit('signatureTypeChange', this.file, signatureType)
    },
    setTemplateDocumentSigners (signers) {
      this.SET_TEMPLATE_DOCUMENT_SIGNERS({
        fileId: this.file.id,
        signers: Number(signers),
      })
    },
    setTemplateDocumentTitle (title) {
      this.SET_TEMPLATE_DOCUMENT_TITLE({
        fileId: this.file.id,
        title: title.trim(),
      })
    },
  },
})
</script>

<style scoped lang="scss">
.EnvelopeTemplateAddSettingsDocument-expansionPanel {
  :deep(.v-expansion-panel-title) {
    border-bottom: 1px solid rgb(var(--v-theme-border-primary));
    border-radius: 6px !important;
  }
  :deep(.v-expansion-panel-text__wrapper) {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
  }
}
</style>
